import React, { useCallback, useState, useMemo, useEffect } from "react";
import TextMoney from "../../components/TextMoney";
import useCartStore from "../../zustands/useCartStore";
import Product from "../../constants/Product";
import { getApiData, getApiErrors } from "../../../frontend/api/http";
import { resolveProductDisplayPreset } from "../../utils/productDisplayPreset";

/**
 * Types matching the API response format from cp-planning /site/orderwindows
 */
type OrderWindowDetailItem = {
    id: number;
    product_id: number;
    window_id: number;
    product_variant_id_list: number[];
    quota_total_quantity: number;
    quota_used?: number;
    description: string;
    display_order: number;
    status: number;
};

type OrderWindowData = {
    id: number;
    name: string;
    title?: string;
    note: string;
    seo_url?: string;
    date_started: number;
    date_cutoff: number;
    date_ended: number;
    date_delivered: number;
    details: OrderWindowDetailItem[];
};

type ApiPriceByUom = { uom_id: number; value: number; min_quantity?: number };
type ApiPriceByPolicy = {
    price_policy_id: number;
    price_by_uom: ApiPriceByUom[];
};

type ApiVariant = {
    id: number;
    sku: string;
    displayname: string;
    baseprice: number;
    sellprice: number;
    compareprice: number;
    discountpercent: number;
    optionvalues: any[];
    pricebypolicy: ApiPriceByPolicy[];
    pricebyuom: ApiPriceByUom[];
    sellpriceforuoms: any[];
    photogalleryfilelist: any[];
};

type ApiPromotionEngine = {
    product_id?: number;
    product_variant_id?: number;
    unit_price?: number;
    unit_price_original?: number;
    product_promotion_decision?: {
        applied?: { id: number; type: string; value: number }[];
        eligible?: { id: number }[];
    };
};

type ApiProduct = {
    id: number;
    name: string;
    seourl: string;
    thumbnails: { url: string }[];
    variants: ApiVariant[];
    selectableoptions: any[];
    productuoms: { uom_id?: number; uomid?: number; uom_name?: string; uomname?: string; factor: number }[];
    weightsalemode: number;
    pricingtype: number;
    weightpresets?: any[];
    promotionengine: ApiPromotionEngine;
};

const DETAIL_STATUS_ENABLE = 1;

// ─── Pricing helpers ───

function getVariantPriceByPolicy(v: ApiVariant, pricePolicyId: number): number {
    if (pricePolicyId > 0 && Array.isArray(v.pricebypolicy)) {
        for (const pp of v.pricebypolicy) {
            if (pp.price_policy_id === pricePolicyId) {
                for (const pu of pp.price_by_uom) {
                    if (pu.uom_id === 0) return pu.value;
                }
            }
        }
    }
    return v.baseprice || 0;
}

function getProductPrice(product: ApiProduct, variant: ApiVariant, pricePolicyId: number) {
    const priceBefore = variant.baseprice || 0;
    let priceSell = getVariantPriceByPolicy(variant, pricePolicyId);
    let finalPriceBefore = priceBefore;

    const pe = product.promotionengine;
    if (pe && typeof pe === "object" && !Array.isArray(pe)) {
        const decision = pe.product_promotion_decision;
        if (decision?.applied && decision.applied.length > 0) {
            if ((pe.unit_price || 0) > 0 && (pe.unit_price || 0) < (pe.unit_price_original || 0)) {
                priceSell = pe.unit_price!;
                finalPriceBefore = pe.unit_price_original!;
            }
        }
    }

    return { priceSell, priceBefore: finalPriceBefore };
}

// ─── Countdown Timer ───

const CountdownTimer: React.FC<{ targetTimestamp: number; label: string }> = ({ targetTimestamp, label }) => {
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));

    useEffect(() => {
        const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
        return () => clearInterval(timer);
    }, []);

    const diff = targetTimestamp - now;
    if (diff <= 0) return <span className="text-xs text-red-600 font-medium">Đã hết hạn</span>;

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ngày`);
    parts.push(`${String(hours).padStart(2, "0")} giờ`);
    parts.push(`${String(minutes).padStart(2, "0")} phút`);
    parts.push(`${String(seconds).padStart(2, "0")} giây`);

    return (
        <span className="text-xs text-gray-500">
            {label} <span className="font-medium text-gray-600">{parts.join(", ")}</span>
        </span>
    );
};

// ─── Quota Progress Bar (compact) ───

const QuotaProgressBar: React.FC<{ used: number; total: number; isKgProduct?: boolean }> = ({ used, total, isKgProduct = false }) => {
    if (total <= 0) return null;

    const displayUsed = isKgProduct ? used / 1000 : used;
    const displayTotal = isKgProduct ? total / 1000 : total;
    const displayRemaining = displayTotal - displayUsed;
    const unit = isKgProduct ? "kg" : "suất";
    const usedPercent = Math.min(100, Math.round((used / total) * 100));

    const barColor = usedPercent >= 100 ? "bg-gray-400" : usedPercent >= 80 ? "bg-red-500" : usedPercent >= 50 ? "bg-orange-400" : "bg-emerald-400";
    const textColor = usedPercent >= 100 ? "text-gray-500" : usedPercent >= 80 ? "text-red-600" : usedPercent >= 50 ? "text-orange-600" : "text-emerald-600";

    return (
        <div className="flex items-center gap-2">
            <span className={`text-[11px] font-medium ${textColor} whitespace-nowrap`}>
                📦 {displayRemaining <= 0 ? `Hết ${unit}` : `Còn ${displayRemaining}/${displayTotal} ${unit}`}
            </span>
            <div className="flex-1 bg-gray-100 rounded-full h-1 overflow-hidden min-w-[40px]">
                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${usedPercent}%` }} />
            </div>
        </div>
    );
};

// ─── Single Order Window Block (compact, no duplicate controls) ───

const OrderWindowBlock: React.FC<{
    orderWindow: OrderWindowData;
    product: ApiProduct;
    selectedVariantId: number | null;
    selectedPreset: any;
    quantity: number;
    pricePolicyId: number;
    allowBuyNow: boolean;
    setCart: (cart: any) => void;
}> = ({
    orderWindow,
    product,
    selectedVariantId,
    selectedPreset,
    quantity,
    pricePolicyId,
    allowBuyNow,
    setCart,
}) => {
        const isKgProduct = useMemo(() => {
            return (
                product.weightsalemode === Product.WEIGHT_SALE_MODE_WEIGHT ||
                product.weightsalemode === Product.WEIGHT_SALE_MODE_COUNTING
            );
        }, [product.weightsalemode]);

        const isCutoffPassed = useMemo(() => {
            return orderWindow.date_cutoff > 0 && Date.now() / 1000 > orderWindow.date_cutoff;
        }, [orderWindow.date_cutoff]);

        // Find matching detail for this product
        const matchingDetail = useMemo(() => {
            return orderWindow.details.find(
                (d) => Number(d.product_id) === Number(product.id) && d.status === DETAIL_STATUS_ENABLE
            );
        }, [orderWindow.details, product.id]);

        // Find variant that matches selectedVariantId + detail's variant list
        const matchedVariant = useMemo(() => {
            if (!matchingDetail || !product.variants || product.variants.length === 0)
                return null;

            const idList = matchingDetail.product_variant_id_list;
            const numericIdList = Array.isArray(idList) ? idList.map(Number).filter(n => n > 0) : [];
            const hasIdList = numericIdList.length > 0;

            if (selectedVariantId && selectedVariantId > 0) {
                const variant = product.variants.find((v) => Number(v.id) === Number(selectedVariantId));
                if (variant) {
                    if (!hasIdList || numericIdList.includes(Number(selectedVariantId))) {
                        return variant;
                    }
                    return null;
                }
            }

            if (hasIdList) {
                return product.variants.find((v) => numericIdList.includes(Number(v.id))) || null;
            }
            return product.variants[0] || null;
        }, [product.variants, selectedVariantId, matchingDetail]);

        if (!matchingDetail || !matchedVariant) return null;

        // Check if product requires variant selection (has selectable options)
        const requiresVariantSelection = useMemo(() => {
            return Array.isArray(product.selectableoptions) && product.selectableoptions.length > 0;
        }, [product.selectableoptions]);

        // Variant is considered selected only if user explicitly chose it
        const isVariantSelected = !requiresVariantSelection || (selectedVariantId !== null && selectedVariantId > 0);

        const quotaTotal = matchingDetail.quota_total_quantity || 0;
        const quotaUsed = matchingDetail.quota_used || 0;
        const isQuotaExhausted = quotaTotal > 0 && quotaUsed >= quotaTotal;
        const isDisabled = isCutoffPassed || isQuotaExhausted || !isVariantSelected;

        const hasPreset = !!selectedPreset;

        const baseUom = useMemo(() => {
            if (!product?.productuoms || product.productuoms.length === 0) return null;
            return product.productuoms.find((u) => u.factor === 1) || product.productuoms[0] || null;
        }, [product]);

        const priceInfo = useMemo(() => {
            if (!matchedVariant) return { priceSell: 0, priceBefore: 0 };
            return getProductPrice(product, matchedVariant, pricePolicyId);
        }, [product, matchedVariant, pricePolicyId]);

        const displayPrice = useMemo(() => {
            if (isKgProduct && selectedPreset && selectedPreset.factor > 0) {
                return priceInfo.priceSell * selectedPreset.factor;
            }
            return isKgProduct ? priceInfo.priceSell * 1000 : priceInfo.priceSell;
        }, [priceInfo, isKgProduct, selectedPreset]);

        const originalPrice = useMemo(() => {
            let before = priceInfo.priceBefore;
            if (isKgProduct && selectedPreset && selectedPreset.factor > 0) {
                before = priceInfo.priceBefore * selectedPreset.factor;
            } else if (isKgProduct) {
                before = priceInfo.priceBefore * 1000;
            }
            return before > displayPrice ? before : 0;
        }, [priceInfo, displayPrice, isKgProduct, selectedPreset]);

        const discountPercent = useMemo(() => {
            if (originalPrice > 0 && displayPrice < originalPrice) {
                return Math.round(((originalPrice - displayPrice) / originalPrice) * 100);
            }
            return 0;
        }, [originalPrice, displayPrice]);

        const windowDisplayName = orderWindow.title || orderWindow.name;

        const [processing, setProcessing] = useState(false);
        const [processingBuyNow, setProcessingBuyNow] = useState(false);
        const [added, setAdded] = useState(false);
        const [errorMsg, setErrorMsg] = useState("");

        const buildPayload = useCallback(() => {
            const presetFactor = (selectedPreset && selectedPreset.factor > 0) ? selectedPreset.factor : 1000;
            const apiPrice = isKgProduct ? displayPrice / presetFactor : displayPrice;
            const apiOriginalPrice = isKgProduct
                ? (originalPrice > 0 ? originalPrice / presetFactor : apiPrice)
                : (originalPrice > 0 ? originalPrice : displayPrice);
            let apiQuantity = quantity;
            if (isKgProduct) {
                if (hasPreset && selectedPreset) {
                    apiQuantity = quantity * selectedPreset.factor;
                } else {
                    apiQuantity = quantity * 1000;
                }
            }
            const uomId = baseUom ? (baseUom.uom_id ?? baseUom.uomid ?? 0) : 0;
            const uomName = baseUom ? (baseUom.uom_name ?? baseUom.uomname ?? "") : "";
            return {
                section: "orderwindow",
                product_variant_id: matchedVariant?.id || 0,
                uom_quantity: apiQuantity,
                uom_id: uomId,
                uom_name: uomName,
                weight_sale_mode: product.weightsalemode || 0,
                weight_preset_id: isKgProduct && selectedPreset ? selectedPreset.id : "",
                weight_preset_name: isKgProduct && selectedPreset ? selectedPreset.name : "",
                weight_preset_factor: isKgProduct && selectedPreset ? selectedPreset.factor : 0,
                pricing_type: product.pricingtype || 0,
                calculated_price: apiPrice,
                selected_options: matchedVariant?.optionvalues || [],
                unit_price_original: apiOriginalPrice,
                unit_price: apiPrice,
                promotionengine: [],
                pricing_context_type: "order_window",
                pricing_context_id: orderWindow.id,
                pricing_context_name: windowDisplayName,
            };
        }, [matchedVariant, baseUom, product, displayPrice, originalPrice, orderWindow.id, windowDisplayName, isKgProduct, hasPreset, selectedPreset, quantity]);

        // Build summary text with variant info
        const variantDisplayText = useMemo(() => {
            if (!matchedVariant) return '';
            if (Array.isArray(matchedVariant.optionvalues) && matchedVariant.optionvalues.length > 0) {
                return matchedVariant.optionvalues.map((ov: any) => ov.option_value_name).join(' / ');
            }
            return '';
        }, [matchedVariant]);

        const summaryText = useMemo(() => {
            const parts: string[] = [`${quantity}`];
            if (isKgProduct && hasPreset && selectedPreset) {
                parts.push(`x ${selectedPreset.name}`);
            } else if (isKgProduct) {
                parts.push(`kg`);
            } else if (variantDisplayText) {
                parts.push(`x ${variantDisplayText}`);
            }
            return parts.join(' ');
        }, [quantity, isKgProduct, hasPreset, selectedPreset, variantDisplayText]);

        const debugTitle = useMemo(() => {
            if (!matchedVariant) return 'Chưa chọn variant';
            return `SKU: ${matchedVariant.sku || 'N/A'} | Variant ID: ${matchedVariant.id}`;
        }, [matchedVariant]);

        const addToCart = useCallback(async () => {
            if (!matchedVariant || !product || isDisabled) return;
            setProcessing(true);
            setErrorMsg("");
            try {
                const res = await fetch("/cart/addajax", {
                    method: "POST",
                    body: JSON.stringify(buildPayload()),
                });
                const data = await res.json();
                const apiData = getApiData<{ cart?: any }>(data);
                const apiErrors = getApiErrors(data);
                if (data.success) {
                    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: apiData?.cart } }));
                    document.dispatchEvent(new CustomEvent("cart:product-added", { detail: { product: { name: product.name } } }));
                    setCart(apiData?.cart);
                    setAdded(true);
                    setTimeout(() => setAdded(false), 2000);
                } else {
                    const msgs = apiErrors.filter((e: string) => !e.startsWith("error_"));
                    setErrorMsg(msgs.length > 0 ? msgs.join(". ") : "Không thể thêm vào giỏ hàng");
                    setTimeout(() => setErrorMsg(""), 5000);
                }
            } catch {
                setErrorMsg("Lỗi kết nối, vui lòng thử lại");
                setTimeout(() => setErrorMsg(""), 5000);
            } finally {
                setProcessing(false);
            }
        }, [matchedVariant, product, isDisabled, buildPayload, setCart]);

        const buyNow = useCallback(async () => {
            if (!matchedVariant || !product || isDisabled) return;
            setProcessingBuyNow(true);
            setErrorMsg("");
            try {
                const res = await fetch("/cartbuynow/initajax", {
                    method: "POST",
                    body: JSON.stringify(buildPayload()),
                });
                const data = await res.json();
                const apiErrors = getApiErrors(data);
                if (data.success) {
                    window.location.href = "/checkout/buynow";
                } else {
                    const msgs = apiErrors.filter((e: string) => !e.startsWith("error_"));
                    setErrorMsg(msgs.length > 0 ? msgs.join(". ") : "Không thể mua ngay");
                    setTimeout(() => setErrorMsg(""), 5000);
                }
            } catch {
                setErrorMsg("Lỗi kết nối, vui lòng thử lại");
                setTimeout(() => setErrorMsg(""), 5000);
            } finally {
                setProcessingBuyNow(false);
            }
        }, [matchedVariant, product, isDisabled, buildPayload]);

        const windowUrl = useMemo(() => {
            if (orderWindow.seo_url) return "/orderwindow/" + orderWindow.seo_url;
            return "/orderwindow?id=" + orderWindow.id;
        }, [orderWindow]);

        const anyProcessing = processing || processingBuyNow;

        return (
            <div className="border border-gray-200 rounded-xl bg-white p-3 space-y-2">
                {/* Row 1: Status + Name (link) + Countdown */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${isCutoffPassed ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isCutoffPassed ? "bg-red-500" : "bg-emerald-500 animate-pulse"}`}></span>
                            {isCutoffPassed ? "Hết hạn" : "Đang mở"}
                        </span>
                        <a href={windowUrl} className="text-sm font-semibold text-gray-800 hover:underline truncate" title={windowDisplayName}>
                            {windowDisplayName}
                        </a>
                        {orderWindow.date_delivered > 0 && (
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">
                                · Giao {(() => { const d = new Date(orderWindow.date_delivered * 1000); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`; })()}
                            </span>
                        )}
                    </div>
                    {orderWindow.date_cutoff > 0 && !isCutoffPassed && (
                        <CountdownTimer targetTimestamp={orderWindow.date_cutoff} label="Hạn đặt:" />
                    )}
                </div>

                {/* Quota */}
                {quotaTotal > 0 && (
                    <QuotaProgressBar used={quotaUsed} total={quotaTotal} isKgProduct={isKgProduct} />
                )}

                {/* Summary: what will be added */}
                {isVariantSelected && !isCutoffPassed && !isQuotaExhausted && (
                    <div className="text-xs text-gray-500" title={debugTitle} data-variant-id={matchedVariant?.id || ''}>
                        Mua theo phiên: <span className="font-medium text-gray-700">{summaryText}</span>
                    </div>
                )}

                {/* Action buttons */}
                {!isVariantSelected && !isCutoffPassed && !isQuotaExhausted ? (
                    <div className="text-xs text-orange-600 font-medium">
                        Vui lòng chọn thuộc tính sản phẩm ở trên
                    </div>
                ) : isCutoffPassed || isQuotaExhausted ? (
                    <div className="text-xs text-gray-500 font-medium">
                        {isCutoffPassed ? "Phiên đã qua hạn đặt" : "Đã hết suất đặt hàng"}
                    </div>
                ) : (
                    <div className="flex items-center gap-2" data-variant-id={matchedVariant?.id || ''}>
                        <button
                            className={`btn-primary text-sm font-medium !py-1.5 !px-4 !leading-6 ${anyProcessing ? "opacity-50 cursor-not-allowed" : added ? "!bg-green-500" : ""}`}
                            disabled={anyProcessing}
                            onClick={addToCart}
                            title={debugTitle}
                        >
                            {processing ? "..." : added ? "✓ Đã thêm" : "Thêm giỏ hàng"}
                        </button>
                        {allowBuyNow && (
                            <button
                                className={`btn-primary text-sm font-medium !py-1.5 !px-4 !leading-6 ${anyProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                                disabled={anyProcessing}
                                onClick={buyNow}
                                title={debugTitle}
                            >
                                {processingBuyNow ? "..." : "Mua ngay"}
                            </button>
                        )}
                    </div>
                )}

                {/* Error */}
                {errorMsg && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                        {errorMsg}
                    </div>
                )}
            </div>
        );
    };

// ─── Main component ───

interface ProductOrderWindowBlocksProps {
    orderWindows: OrderWindowData[];
    product: ApiProduct;
    pricePolicyId: number;
    allowBuyNow: boolean;
    defaultWeightPresets: any[];
}

const ProductOrderWindowBlocks: React.FC<ProductOrderWindowBlocksProps> = ({
    orderWindows,
    product,
    pricePolicyId,
    allowBuyNow,
    defaultWeightPresets,
}) => {
    const setCart = useCartStore((state) => state.setCart);
    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
    const [selectedPreset, setSelectedPreset] = useState<any>(null);

    const [quantity, setQuantity] = useState(1);

    // Listen to product:option-changed event from ProductDetail to track selected variant & preset
    useEffect(() => {
        const handler = (e: any) => {
            const detail = e.detail;
            if (detail?.selectedVariant?.id) {
                setSelectedVariantId(detail.selectedVariant.id);
            } else {
                setSelectedVariantId(null);
            }
        };
        window.addEventListener("product:option-changed", handler);
        return () => window.removeEventListener("product:option-changed", handler);
    }, []);

    // Listen to preset changes from ProductDetail
    useEffect(() => {
        const handler = (e: any) => {
            const detail = e.detail;
            setSelectedPreset(detail?.selectedPreset || null);
        };
        window.addEventListener("product:preset-changed", handler);
        return () => window.removeEventListener("product:preset-changed", handler);
    }, []);

    // Listen to quantity changes from ProductAddToCart
    useEffect(() => {
        const handler = (e: any) => {
            const detail = e.detail;
            if (detail?.quantity && detail.quantity > 0) {
                setQuantity(detail.quantity);
            }
        };
        window.addEventListener("product:quantity-changed", handler);
        return () => window.removeEventListener("product:quantity-changed", handler);
    }, []);

    // If no preset from event, auto-select from product or defaults
    const effectivePreset = useMemo(() => {
        if (selectedPreset) return selectedPreset;
        const isKg = product.weightsalemode === Product.WEIGHT_SALE_MODE_WEIGHT || product.weightsalemode === Product.WEIGHT_SALE_MODE_COUNTING;
        if (!isKg) return null;
        const displayPreset = resolveProductDisplayPreset(product);
        if (displayPreset) return displayPreset;
        if (Array.isArray(defaultWeightPresets) && defaultWeightPresets.length > 0) return defaultWeightPresets[0];
        return null;
    }, [selectedPreset, product, defaultWeightPresets]);

    if (!orderWindows || orderWindows.length === 0) return null;

    return (
        <div className="mt-5 space-y-3">
            {orderWindows.map((ow) => (
                <OrderWindowBlock
                    key={ow.id}
                    orderWindow={ow}
                    product={product}
                    selectedVariantId={selectedVariantId}
                    selectedPreset={effectivePreset}
                    quantity={quantity}
                    pricePolicyId={pricePolicyId}
                    allowBuyNow={allowBuyNow}
                    setCart={setCart}
                />
            ))}
        </div>
    );
};

export default ProductOrderWindowBlocks;
